const PRICE_MATCH_TOLERANCE_CENTS = 1;
const DEFAULT_UK_VAT_MULTIPLIER = 1.2;
const SUPPORTED_REFERENCE_MARKETS = new Set(['uk', 'us', 'europe', 'row']);
const SUPPORTED_PRODUCT_GROUPS = new Set(['accessories', 'fabrics_wallpaper', 'other']);

function roundMinorUnits(value) {
  const parsedValue = Number(value);
  if (!Number.isFinite(parsedValue)) return null;

  return Math.round(parsedValue);
}

function parseMinorUnitValue(rawValue) {
  if (rawValue === null || rawValue === undefined || rawValue === '') return null;
  return roundMinorUnits(rawValue);
}

function parseMajorAmountToMinorUnits(rawAmount) {
  const parsedAmount = Number(rawAmount);
  if (!Number.isFinite(parsedAmount)) return null;

  return Math.round(parsedAmount * 100);
}

function getAbsoluteDifference(leftValue, rightValue) {
  return Math.abs(leftValue - rightValue);
}

function normalizeWholesaleDiscounts(rawDiscounts) {
  let parsedDiscounts = rawDiscounts;

  if (typeof parsedDiscounts === 'string') {
    const trimmedDiscounts = parsedDiscounts.trim();
    if (trimmedDiscounts === '') return [];

    try {
      parsedDiscounts = JSON.parse(trimmedDiscounts);
    } catch (error) {
      return [];
    }
  }

  if (!Array.isArray(parsedDiscounts)) {
    return [];
  }

  const seenDiscounts = new Set();
  const normalizedDiscounts = [];

  parsedDiscounts.forEach((rawDiscount) => {
    const parsedDiscount = Number(rawDiscount);

    // Market metafields store decimal wholesale discounts, where 0 means "no discount".
    if (!Number.isFinite(parsedDiscount) || parsedDiscount <= 0 || parsedDiscount >= 1) {
      return;
    }

    const normalizedKey = parsedDiscount.toFixed(6);
    if (seenDiscounts.has(normalizedKey)) return;

    seenDiscounts.add(normalizedKey);
    normalizedDiscounts.push(parsedDiscount);
  });

  return normalizedDiscounts;
}

function normalizeTradeDiscount(rawDiscount) {
  if (rawDiscount === null || rawDiscount === undefined || rawDiscount === '') {
    return null;
  }

  const parsedDiscount = Number(rawDiscount);
  if (!Number.isFinite(parsedDiscount) || parsedDiscount < 0 || parsedDiscount >= 1) {
    return null;
  }

  return parsedDiscount;
}

function inferB2bRrp({
  retailPrice,
  b2bPrice,
  market,
  productGroup,
  customerSegment = '',
  tradeDiscount = null,
  wholesaleDiscounts = [],
  toleranceCents = PRICE_MATCH_TOLERANCE_CENTS,
} = {}) {
  const retailPriceCents = parseMinorUnitValue(retailPrice);
  const b2bPriceCents = parseMinorUnitValue(b2bPrice);
  const normalizedMarket = typeof market === 'string' ? market.trim().toLowerCase() : '';
  const normalizedProductGroup =
    typeof productGroup === 'string' ? productGroup.trim().toLowerCase() : '';
  const normalizedCustomerSegment =
    typeof customerSegment === 'string' ? customerSegment.trim().toLowerCase() : '';
  const normalizedToleranceCents = Math.max(
    0,
    roundMinorUnits(toleranceCents) ?? PRICE_MATCH_TOLERANCE_CENTS
  );
  const normalizedTradeDiscount = normalizeTradeDiscount(tradeDiscount);
  const normalizedWholesaleDiscounts = normalizeWholesaleDiscounts(wholesaleDiscounts);
  const wholesaleUsesTradeReference = normalizedProductGroup === 'fabrics_wallpaper';

  if (!Number.isFinite(retailPriceCents) || retailPriceCents <= 0) {
    return {
      matched: false,
      inferredTier: null,
      referenceTier: null,
      rrpPrice: null,
      matchedRule: null,
      reason: 'missing-retail-price',
      candidateMatches: [],
    };
  }

  if (!Number.isFinite(b2bPriceCents) || b2bPriceCents <= 0) {
    return {
      matched: false,
      inferredTier: null,
      referenceTier: null,
      rrpPrice: null,
      matchedRule: null,
      reason: 'missing-b2b-price',
      candidateMatches: [],
    };
  }

  if (
    !SUPPORTED_REFERENCE_MARKETS.has(normalizedMarket) ||
    !SUPPORTED_PRODUCT_GROUPS.has(normalizedProductGroup)
  ) {
    return {
      matched: false,
      inferredTier: null,
      referenceTier: null,
      rrpPrice: null,
      matchedRule: null,
      reason: 'unsupported-context',
      candidateMatches: [],
    };
  }

  if (normalizedTradeDiscount === null && wholesaleUsesTradeReference) {
    return {
      matched: false,
      inferredTier: null,
      referenceTier: null,
      rrpPrice: null,
      matchedRule: null,
      reason: 'missing-trade-discount',
      candidateMatches: [],
    };
  }

  const tradePriceCents =
    normalizedTradeDiscount === null
      ? null
      : roundMinorUnits(retailPriceCents * (1 - normalizedTradeDiscount));
  const candidateMatches = [];

  if (Number.isFinite(tradePriceCents)) {
    candidateMatches.push({
      inferredTier: 'trade',
      referenceTier: 'retail',
      expectedB2bPrice: tradePriceCents,
      rrpPrice: retailPriceCents,
      tradeDiscount: normalizedTradeDiscount,
      matchedRule: `${normalizedMarket}.${normalizedProductGroup}.trade`,
    });
  }

  // The active market wholesale metafield list behaves differently by product group:
  // - accessories / other: discounts are applied directly against retail/base
  // - fabrics_wallpaper: discounts are applied against the inferred trade price
  // A value of 0 means "no discount", so it is filtered out before candidate building.
  normalizedWholesaleDiscounts.forEach((wholesaleDiscount) => {
      const wholesaleMultiplier = 1 - wholesaleDiscount;
      const wholesaleReferencePriceCents = wholesaleUsesTradeReference
        ? tradePriceCents
        : retailPriceCents;

      if (!Number.isFinite(wholesaleReferencePriceCents)) return;

      const wholesalePriceCents = roundMinorUnits(
        wholesaleReferencePriceCents * wholesaleMultiplier
      );
      if (!Number.isFinite(wholesalePriceCents)) return;

      candidateMatches.push({
        inferredTier: 'wholesale',
        expectedB2bPrice: wholesalePriceCents,
        referenceTier: wholesaleUsesTradeReference ? 'trade' : 'retail',
        rrpPrice: wholesaleReferencePriceCents,
        tradeDiscount: normalizedTradeDiscount,
        wholesaleDiscount,
        matchedRule: `${normalizedMarket}.${normalizedProductGroup}.wholesale.discount_${String(
          wholesaleDiscount
        ).replace('.', '_')}`,
      });
  });

  // Trust only exact minor-unit ladder matches within the configured tolerance.
  // If more than one candidate matches, the result is ambiguous and must fail safe.
  const matchingCandidates = candidateMatches
    .map((candidate) => ({
      ...candidate,
      differenceCents: getAbsoluteDifference(b2bPriceCents, candidate.expectedB2bPrice),
    }))
    .filter((candidate) => candidate.differenceCents <= normalizedToleranceCents);

  if (!matchingCandidates.length) {
    return {
      matched: false,
      inferredTier: null,
      referenceTier: null,
      rrpPrice: null,
      matchedRule: null,
      reason: 'no-match',
      candidateMatches,
    };
  }

  if (matchingCandidates.length > 1) {
    return {
      matched: false,
      inferredTier: null,
      referenceTier: null,
      rrpPrice: null,
      matchedRule: null,
      reason: 'ambiguous-match',
      candidateMatches,
    };
  }

  const [matchedCandidate] = matchingCandidates;

  if (normalizedCustomerSegment === 'wholesale') {
    if (!Number.isFinite(tradePriceCents) || tradePriceCents <= 0) {
      return {
        matched: false,
        inferredTier: matchedCandidate.inferredTier,
        referenceTier: null,
        rrpPrice: null,
        matchedRule: matchedCandidate.matchedRule,
        reason: 'missing-trade-reference',
        candidateMatches,
      };
    }

    return {
      matched: true,
      inferredTier: matchedCandidate.inferredTier,
      referenceTier: 'trade',
      rrpPrice: tradePriceCents,
      matchedRule: matchedCandidate.matchedRule,
      differenceCents: matchedCandidate.differenceCents,
      differenceMultiplier: null,
      candidateMatches,
    };
  }

  return {
    matched: true,
    inferredTier: matchedCandidate.inferredTier,
    referenceTier: matchedCandidate.referenceTier,
    rrpPrice: matchedCandidate.rrpPrice,
    matchedRule: matchedCandidate.matchedRule,
    differenceCents: matchedCandidate.differenceCents,
    differenceMultiplier: null,
    candidateMatches,
  };
}

if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    PRICE_MATCH_TOLERANCE_CENTS,
    inferB2bRrp,
    normalizeTradeDiscount,
    normalizeWholesaleDiscounts,
    parseMajorAmountToMinorUnits,
    parseMinorUnitValue,
    roundMinorUnits,
  };
}

if (typeof document !== 'undefined') {
  (() => {
    // Always fetch the selected variant through the unauthenticated Storefront API path.
    // That gives us the live active-market retail/base price to compare against the
    // contextual B2B price already rendered into the PDP by Shopify.
    const STOREFRONT_VARIANT_RETAIL_PRICE_QUERY = `#graphql
      query PMVariantRetailPrice($id: ID!, $country: CountryCode)
      @inContext(country: $country) {
        node(id: $id) {
          ... on ProductVariant {
            id
            price {
              amount
              currencyCode
            }
          }
        }
      }
    `;

    const UK_VAT_MULTIPLIER =
      window.PMUKRetailPricing?.UK_VAT_MULTIPLIER || DEFAULT_UK_VAT_MULTIPLIER;
    const retailPriceRequestCache = new Map();

    function getDatasetValue(row, key) {
      const value = row?.dataset?.[key];
      return typeof value === 'string' ? value.trim() : '';
    }

    function getStorefrontAccessToken() {
      const configToken = window.pmStorefrontApiConfig?.accessToken;
      if (typeof configToken === 'string' && configToken.trim() !== '') {
        return configToken.trim();
      }

      const legacyToken = window.pmStorefrontEstimatorConfig?.accessToken;
      if (typeof legacyToken === 'string' && legacyToken.trim() !== '') {
        return legacyToken.trim();
      }

      return '';
    }

    function getStorefrontApiVersion() {
      const configVersion = window.pmStorefrontApiConfig?.apiVersion;
      if (typeof configVersion === 'string' && /^\d{4}-\d{2}$/.test(configVersion.trim())) {
        return configVersion.trim();
      }

      const legacyVersion = window.pmStorefrontEstimatorConfig?.apiVersion;
      if (typeof legacyVersion === 'string' && /^\d{4}-\d{2}$/.test(legacyVersion.trim())) {
        return legacyVersion.trim();
      }

      return '2025-01';
    }

    function getStorefrontGraphQLEndpoint() {
      return `/api/${getStorefrontApiVersion()}/graphql.json`;
    }

    async function requestStorefrontGraphQL(query, variables = {}) {
      const accessToken = getStorefrontAccessToken();
      if (!accessToken) {
        throw new Error('Missing Storefront API access token.');
      }

      const response = await fetch(getStorefrontGraphQLEndpoint(), {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': accessToken,
        },
        body: JSON.stringify({
          query,
          variables,
        }),
      });

      if (!response.ok) {
        throw new Error('PDP B2B RRP Storefront request failed.');
      }

      const payload = await response.json();
      if (Array.isArray(payload?.errors) && payload.errors.length > 0) {
        throw new Error(payload.errors[0]?.message || 'PDP B2B RRP Storefront query failed.');
      }

      return payload?.data || {};
    }

    async function fetchVariantRetailPrice(variantGid, countryCode) {
      const normalizedVariantGid = typeof variantGid === 'string' ? variantGid.trim() : '';
      if (!normalizedVariantGid) {
        return null;
      }

      const normalizedCountryCode =
        typeof countryCode === 'string' && countryCode.trim() !== ''
          ? countryCode.trim().toUpperCase()
          : null;
      const cacheKey = `${normalizedVariantGid}::${normalizedCountryCode || ''}`;

      if (!retailPriceRequestCache.has(cacheKey)) {
        const request = requestStorefrontGraphQL(STOREFRONT_VARIANT_RETAIL_PRICE_QUERY, {
          id: normalizedVariantGid,
          country: normalizedCountryCode,
        }).then((data) => data?.node?.price || null);

        retailPriceRequestCache.set(cacheKey, request);
      }

      return retailPriceRequestCache.get(cacheKey);
    }

    function formatDisplayPrice(amount, currencyCode, countryCode, languageCode, withCurrency) {
      if (window.PMLocalizedMoney?.formatAmount) {
        return window.PMLocalizedMoney.formatAmount(amount, {
          currencyCode,
          countryCode,
          languageCode,
          withCurrency,
        });
      }

      try {
        const normalizedCurrencyCode =
          typeof currencyCode === 'string' && currencyCode.trim() !== ''
            ? currencyCode.trim().toUpperCase()
            : 'USD';
        const locale =
          typeof languageCode === 'string' && languageCode.trim() !== ''
            ? languageCode.trim()
            : undefined;
        const formatter = new Intl.NumberFormat(locale, {
          style: 'currency',
          currency: normalizedCurrencyCode,
          currencyDisplay: withCurrency ? 'code' : 'symbol',
        });

        return formatter.format(amount);
      } catch (error) {
        return String(amount);
      }
    }

    async function resolveRetailReference(row, variantGid, countryCode) {
      // The inference model is only trustworthy when retail comes from Shopify's live
      // non-buyer Storefront price for the same variant and country context.
      const retailPrice = await fetchVariantRetailPrice(variantGid, countryCode);
      const retailPriceCents = parseMajorAmountToMinorUnits(retailPrice?.amount);
      const retailCurrencyCode = retailPrice?.currencyCode || row.dataset.marketCurrencyCode || '';

      return {
        retailPriceCents,
        retailCurrencyCode,
      };
    }

    async function enhanceRrpRow(row) {
      if (!(row instanceof HTMLElement)) return;
      if (row.dataset.rrpEnhancementState === 'loading') return;

      const variantGid = row.dataset.variantGid;
      const countryCode = row.dataset.marketCountryCode || '';
      const languageCode = row.dataset.languageCode || document.documentElement.lang || '';
      const withCurrency = row.dataset.moneyWithCurrency === 'true';
      const currentB2bPriceCents = parseMinorUnitValue(row.dataset.currentB2bPriceCents);
      const region = getDatasetValue(row, 'marketRegion');
      const productGroup = getDatasetValue(row, 'productGroup');
      const customerSegment = getDatasetValue(row, 'customerSegment');
      const labelElement = row.querySelector('[data-pdp-b2b-rrp-label]');
      const valueElement = row.querySelector('[data-pdp-b2b-rrp-value]');
      const unitElement = row.querySelector('[data-pdp-b2b-rrp-unit]');

      if (!variantGid || !valueElement || !labelElement) return;

      delete row.dataset.pdpB2bRrpPending;
      row.removeAttribute('aria-hidden');
      labelElement.textContent = '';
      valueElement.textContent = '';
      if (unitElement instanceof HTMLElement) {
        unitElement.hidden = true;
      }

      if (!Number.isFinite(currentB2bPriceCents) || currentB2bPriceCents <= 0) {
        row.hidden = true;
        row.dataset.rrpEnhancementState = 'invalid';
        return;
      }

      row.dataset.rrpEnhancementState = 'loading';

      try {
        if (getStorefrontAccessToken() === '') {
          row.hidden = true;
          row.dataset.rrpEnhancementState = 'unavailable';
          return;
        }

        const { retailPriceCents, retailCurrencyCode } = await resolveRetailReference(
          row,
          variantGid,
          countryCode
        );

        if (
          !Number.isFinite(retailPriceCents) ||
          retailPriceCents <= 0 ||
          retailCurrencyCode === ''
        ) {
          throw new Error('Retail/base price was unavailable for the selected variant.');
        }

        // Deterministic order:
        // 1. Use the live retail/base price for this exact variant + market.
        // 2. Compare the current contextual B2B price against the configured ladders.
        // 3. Only surface an RRP when one candidate wins unambiguously.
        const inference = inferB2bRrp({
          retailPrice: retailPriceCents,
          b2bPrice: currentB2bPriceCents,
          market: region,
          productGroup,
          customerSegment,
          tradeDiscount: row.dataset.tradeDiscount || '',
          wholesaleDiscounts: row.dataset.wholesaleDiscounts || '[]',
        });

        if (!inference.matched || !Number.isFinite(inference.rrpPrice)) {
          row.hidden = true;
          row.dataset.rrpEnhancementState =
            inference.reason === 'ambiguous-match' ? 'ambiguous' : 'unmatched';
          return;
        }

        let displayedRrpCents = inference.rrpPrice;
        if (row.dataset.ukRetailAdjustment === 'true') {
          displayedRrpCents = roundMinorUnits(displayedRrpCents * UK_VAT_MULTIPLIER);
        }

        const formattedPrice = formatDisplayPrice(
          displayedRrpCents / 100,
          retailCurrencyCode,
          countryCode,
          languageCode,
          withCurrency
        );
        const resolvedLabel =
          inference.referenceTier === 'trade'
            ? getDatasetValue(row, 'tradeReferenceLabel')
            : getDatasetValue(row, 'retailReferenceLabel');

        if (resolvedLabel !== '') {
          labelElement.textContent = resolvedLabel;
        }
        valueElement.textContent = formattedPrice;
        if (unitElement instanceof HTMLElement) {
          unitElement.hidden = false;
        }
        row.hidden = false;
        row.dataset.rrpEnhancementState = 'loaded';
      } catch (error) {
        row.hidden = true;
        row.dataset.rrpEnhancementState = 'error';
        console.error('[PdpB2BRrp] Failed to infer PDP B2B RRP', error);
      }
    }

    function enhanceRowsWithin(root = document) {
      root.querySelectorAll('[data-pdp-b2b-rrp]').forEach((row) => {
        enhanceRrpRow(row);
      });
    }

    function initialize() {
      enhanceRowsWithin(document);

      if (
        typeof subscribe === 'function' &&
        typeof PUB_SUB_EVENTS !== 'undefined' &&
        PUB_SUB_EVENTS?.variantChange
      ) {
        subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const sectionId = event?.data?.sectionId;
          if (!sectionId) return;

          const priceNode = document.getElementById(`price-${sectionId}`);
          if (!priceNode) return;

          enhanceRowsWithin(priceNode);
        });
      }

      document.addEventListener('product-info:loaded', (event) => {
        const root = event?.target instanceof HTMLElement ? event.target : document;
        enhanceRowsWithin(root);
      });
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initialize, { once: true });
    } else {
      initialize();
    }
  })();
}
